import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

export const CustomBuildForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/custom-build/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl glass rounded-2xl p-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {!submitted ? (
          <>
            <h2 className="text-3xl font-headline font-bold text-[#F4F4F5] mb-2">
              Start Your AI Build Project
            </h2>
            <p className="text-[#A1A1AA] font-body mb-6">
              Tell us about your project and we will create a custom proposal within 48 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Project Type *
                </label>
                <select
                  required
                  value={formData.projectType}
                  onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                >
                  <option value="">Select project type</option>
                  <option value="lead-automation">Lead Capture & Automation</option>
                  <option value="customer-service">AI Customer Service Agent</option>
                  <option value="workflow-automation">Workflow Automation</option>
                  <option value="data-integration">Data Integration & Sync</option>
                  <option value="custom-agent">Custom AI Agent</option>
                  <option value="full-system">Full AI System Overhaul</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Budget Range *
                </label>
                <select
                  required
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                >
                  <option value="">Select budget range</option>
                  <option value="10k-25k">$10K - $25K</option>
                  <option value="25k-50k">$25K - $50K</option>
                  <option value="50k-100k">$50K - $100K</option>
                  <option value="100k-plus">$100K+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Desired Timeline *
                </label>
                <select
                  required
                  value={formData.timeline}
                  onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                >
                  <option value="">Select timeline</option>
                  <option value="asap">ASAP (4-6 weeks)</option>
                  <option value="2-months">Within 2 months</option>
                  <option value="3-months">Within 3 months</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Project Description *
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Tell us about what you want to build and the problems you are trying to solve..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599] resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00E599] hover:bg-[#00C985] text-[#050505] px-8 py-4 text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Request Custom Proposal'}
              </Button>

              <p className="text-xs text-[#A1A1AA] text-center font-body">
                By submitting, you agree to receive communications from Smart Growth Tech. We respect your privacy.
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#00E599] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#050505]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-headline font-bold text-[#F4F4F5] mb-2">
              Proposal Request Received!
            </h3>
            <p className="text-[#A1A1AA] font-body">
              Our team will review your project requirements and send you a custom proposal within 48 hours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomBuildForm;
